import { LogoffPage } from '@repo/auth/react';
import { BackLight } from '@repo/go-icons';
import { ConfigProvider, Loader } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { I18nextProvider } from 'react-i18next';

import { useServiceAccessor } from 'src/packages/di';
import 'src/packages/assets/styles/index.scss';
import { AccessDeniedModal } from 'src/pages/dashboard/features/access-denied-modal/AccessDeniedModal';
import { ConnectionClosedModal } from 'src/pages/dashboard/features/connection-closed-modal/ConnectionClosedModal';

import styles from './App.module.scss';
import { authStore } from './auth/AuthStore';
import i18n from './i18n';
import { RootStore, RootStoreProvider } from './root-store';
import { initAppServices } from './root-store/initAppServices';
import Router from './router/Router';
import { getLocale } from './router/utils';

export const App: FC = observer(function App() {
  const [store, setStore] = useState<RootStore | null>(null);
  const [error, setError] = useState<'Forbidden' | 'Unknown' | null>(null);

  const getTheme = useServiceAccessor('theme');
  const getLanguage = useServiceAccessor('language');
  const getAuthService = useServiceAccessor('authService');

  useEffect(() => {}, [getTheme]);

  useEffect(() => {
    async function init(): Promise<void> {
      if (window.location.pathname === '/logoff') {
        return;
      }

      await authStore.init();

      if (!authStore.userInfo) {
        return;
      }

      const message = await initAppServices();
      if (message === 'Forbidden') {
        setError('Forbidden');
        return;
      }
      if (message === 'Unknown') {
        setError('Unknown');
        return;
      }

      setStore(new RootStore());
    }

    init();
  }, [getTheme]);

  if (window.location.pathname === '/logoff') {
    return (
      <I18nextProvider i18n={i18n}>
        <LogoffPage />
      </I18nextProvider>
    );
  }

  if (error === 'Forbidden') {
    return (
      <ConfigProvider
        config={{
          theme: getTheme().theme,
          style: 'bordered',
          localization: getLocale(getLanguage().language),
          size: 's',
        }}
        externalElement={document.body}
      >
        <ConnectionClosedModal
          isOpened={true}
          titleT="modals:AccessDenied.title"
          descT="modals:AccessDenied.desc"
          externalAction={() => getAuthService().logout()}
          externalActionT="modals:AccessDenied.bactToLogin"
          externalActionIcon={<BackLight />}
        />
      </ConfigProvider>
    );
  }

  if (error === 'Unknown') {
    return (
      <ConfigProvider
        config={{
          theme: 'dark',
          style: 'bordered',
          size: 's',
        }}
        externalElement={document.body}
      >
        <AccessDeniedModal
          isOpened={true}
          titleT="modals:AccessDeniedUnknown.title"
          descT="modals:AccessDeniedUnknown.desc"
        />
      </ConfigProvider>
    );
  }

  if (!store) {
    return (
      <ConfigProvider
        config={{
          theme: 'dark',
          style: 'bordered',
          size: 's',
        }}
        externalElement={document.body}
      >
        <div className={styles.loaderContainer}>
          <Loader size="l" />
        </div>
      </ConfigProvider>
    );
  }

  return (
    <RootStoreProvider store={store}>
      <I18nextProvider i18n={i18n}>
        <Router />
      </I18nextProvider>
    </RootStoreProvider>
  );
});
